import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { signIn, signOut } from 'next-auth/react';
import type { SignInResponse } from 'next-auth/react';
import { useTranslations } from 'next-intl';

import { apiClient } from '@/lib/api';
import { AppLogger } from '@/lib/logger';

import {
  createApiMutationHook,
  createIdempotentMutationHook,
} from './useApiMutation';
import { useQueryErrorHandler } from './useQueryErrorHandler';

// Types for auth operations
export interface RegisterData {
  email: string;
  password: string;
}

export interface RegisterResponse {
  ok: boolean;
  user: {
    id: string;
    email: string;
  };
}

export interface LoginData {
  email: string;
  password: string;
  callbackUrl?: string;
}

export interface LoginResponse {
  ok: boolean;
  url?: string | null;
  error?: string;
}

/**
 * Registers a new user account.
 *
 * Protected by idempotency to prevent duplicate account creation.
 * Bcrypt hashing (12 rounds) is expensive, making idempotency especially valuable.
 *
 * @param data - Registration data (email, password)
 * @param context - Idempotent mutation context with auto-generated key
 * @returns Registration response with user ID and email
 */
async function registerUserMutation(
  data: RegisterData,
  { idempotencyKey }: { idempotencyKey: string }
): Promise<RegisterResponse> {
  return apiClient.post<RegisterResponse>(
    '/api/auth/actRegister',
    data,
    undefined,
    undefined,
    { idempotencyKey }
  );
}

/**
 * Authenticates a user with email and password via NextAuth.
 *
 * Note: Login is naturally idempotent via NextAuth's session management.
 * Using legacy mutation hook as no server-side idempotency is needed.
 *
 * @param data - Login credentials (email, password, optional callbackUrl)
 * @returns NextAuth sign-in response with URL and error status
 */
async function loginMutation(data: LoginData): Promise<SignInResponse> {
  const result = await signIn('credentials', {
    email: data.email,
    password: data.password,
    redirect: false,
  });

  // Check if NextAuth returned an error
  if (result?.error) {
    throw new Error(result.error);
  }

  // Return the result with the callbackUrl for the onSuccess handler
  return {
    ...result,
    url: data.callbackUrl || result?.url,
  };
}

// ============================================================================
// Public API - Exports
// ============================================================================

/**
 * React Query hook for user registration with automatic idempotency.
 *
 * Prevents duplicate account creation and protects expensive bcrypt operations.
 * Idempotency keys are automatically generated and managed.
 *
 * @example
 * const register = useRegisterMutation();
 * register.mutate({
 *   email: 'user@example.com',
 *   password: 'securePassword123'
 * });
 */
export const useRegisterMutation = createIdempotentMutationHook<
  RegisterResponse,
  RegisterData
>(registerUserMutation);

/**
 * React Query hook for user login via NextAuth.
 *
 * Note: Login is naturally idempotent - NextAuth handles session management.
 *
 * @example
 * const login = useLoginMutation();
 * login.mutate({
 *   email: 'user@example.com',
 *   password: 'securePassword123'
 * });
 */
export const useLoginMutation = createApiMutationHook<
  SignInResponse,
  LoginData
>(loginMutation);

/**
 * Composite auth hook with registration, login, and sign-out functionality.
 *
 * Provides a unified interface for all authentication operations with
 * automatic redirect handling and error logging.
 *
 * @example
 * const { register, login, signOut } = useAuth();
 *
 * // Register new user (auto-login after success)
 * register.mutate({ email: 'user@example.com', password: 'pass123' });
 *
 * // Login existing user
 * login.mutate({ email: 'user@example.com', password: 'pass123' });
 *
 * // Sign out
 * signOut.mutate();
 */
export function useAuth() {
  const router = useRouter();
  const t = useTranslations('auth');

  // Standardized error handler for sign-out errors
  const handleError = useQueryErrorHandler({
    sentryTags: { feature: 'auth', action: 'signOut' },
  });

  const register = useRegisterMutation({
    onSuccess: async (_data, variables) => {
      // Auto-login after successful registration
      const signInResult = await signIn('credentials', {
        email: variables.email,
        password: variables.password,
        redirect: false,
      });

      if (signInResult?.error) {
        // NextAuth failed - log and throw error with translated message
        AppLogger.error('Auto-login after registration failed', undefined, {
          nextAuthError: signInResult.error,
          correlationId: `auth_register_autologin_${Date.now()}`,
        });
        throw new Error(t('registration.autoLoginFailed'));
      }

      // NextAuth succeeded - redirect to dashboard
      router.push('/en/dashboards');
    },
    onError: error => {
      // Log error to console with context (dev debugging)
      AppLogger.error(
        'Registration failed',
        error instanceof Error ? error : undefined,
        {
          correlationId: `auth_register_${Date.now()}`,
        }
      );
      // The error will be caught by React Query and handled by the mutation factory
    },
  });

  const login = useLoginMutation({
    onSuccess: data => {
      // Redirect to the callbackUrl from the original data
      router.push(data.url || '/en/dashboards');
    },
    onError: error => {
      // Log error to Sentry with context
      AppLogger.error('NextAuth login failed', error, {
        correlationId: `auth_login_${Date.now()}`,
      });
      // The error will be caught by React Query and displayed in the UI
    },
  });

  // Sign out mutation hook with standardized error handling
  const signOutMutation = useMutation({
    mutationFn: async () => {
      return signOut({ callbackUrl: '/en/login' });
    },
    onSuccess: () => {
      // Redirect is handled by NextAuth, but we can add custom logic here if needed
    },
    onError: (error: Error) => {
      // Call standardized error handler (toast + Sentry)
      handleError(error);
      // Keep legacy console.error for debugging
      console.error('🔐 [useAuth] Sign out error:', error);
    },
  });

  return {
    register,
    login,
    signOut: signOutMutation,
  };
}
